import { ReferralNetwork, Employee } from '../data/mockReferralData'
import { ReferralStats } from './referralCalculations'

// Executive metric types
export type ExecutiveKPI = {
  label: string
  value: string
  subValue?: string
  trend: number // percentage change, positive or negative
  trendLabel: string
}

export type DepartmentFunding = {
  department: string
  totalFunding: number
  employeeCount: number
  color: string
}

export type TopPerformer = {
  name: string
  directFundings: number
  indirectFundings: number
  totalImpact: number
}

// Department colors for charts
const DEPARTMENT_COLORS: Record<string, string> = {
  Originations: '#3b82f6', // blue
  Sales: '#10b981', // emerald
  Operations: '#8b5cf6', // purple
  Processing: '#14b8a6', // teal
  Underwriting: '#f59e0b', // amber
  Analytics: '#ec4899', // pink
}

/**
 * Calculate department-level funding aggregations
 */
export function calculateDepartmentFundings(
  network: ReferralNetwork,
): DepartmentFunding[] {
  const departmentMap = new Map<
    string,
    { totalFunding: number; employeeCount: number }
  >()

  // Aggregate by department
  Object.values(network.employees).forEach((employee) => {
    const existing = departmentMap.get(employee.department) || {
      totalFunding: 0,
      employeeCount: 0,
    }
    departmentMap.set(employee.department, {
      totalFunding: existing.totalFunding + employee.fundingAmount,
      employeeCount: existing.employeeCount + 1,
    })
  })

  // Convert to array with colors
  return Array.from(departmentMap.entries())
    .map(([department, data]) => ({
      department,
      totalFunding: data.totalFunding,
      employeeCount: data.employeeCount,
      color: DEPARTMENT_COLORS[department] || '#6b7280',
    }))
    .sort((a, b) => b.totalFunding - a.totalFunding)
}

/**
 * Get top performers by total network impact (direct + indirect fundings)
 */
export function getTopPerformers(
  stats: ReferralStats[],
  limit: number = 5,
): TopPerformer[] {
  return stats
    .filter((s) => s.directFundings > 0 || s.indirectFundings > 0)
    .sort(
      (a, b) =>
        b.directFundings +
        b.indirectFundings -
        (a.directFundings + a.indirectFundings),
    )
    .slice(0, limit)
    .map((s) => ({
      name: s.employee.name.split(' ')[0], // First name only for chart
      directFundings: s.directFundings,
      indirectFundings: s.indirectFundings,
      totalImpact: s.directFundings + s.indirectFundings,
    }))
}

/**
 * Calculate executive KPIs with mock trend data
 */
export function calculateExecutiveKPIs(
  stats: ReferralStats[],
  network: ReferralNetwork,
): {
  totalNetworkValue: ExecutiveKPI
  activeReferrers: ExecutiveKPI
  avgROIPerReferral: ExecutiveKPI
  networkGrowth: ExecutiveKPI
} {
  const totalDirectFundings = stats.reduce(
    (sum, s) => sum + s.directFundings,
    0,
  )
  const totalIndirectFundings = stats.reduce(
    (sum, s) => sum + s.indirectFundings,
    0,
  )
  const totalNetworkValue = totalDirectFundings + totalIndirectFundings

  const activeReferrers = stats.filter((s) => s.totalNetworkImpact > 0).length
  const totalEmployees = stats.length
  const participationRate = Math.round((activeReferrers / totalEmployees) * 100)

  const totalReferrals = stats.reduce(
    (sum, s) => sum + s.directCount + s.indirectCount,
    0,
  )
  const avgROI =
    totalReferrals > 0 ? Math.round(totalNetworkValue / totalReferrals) : 0

  return {
    totalNetworkValue: {
      label: 'Total Network Value',
      value: formatCurrency(totalNetworkValue),
      subValue: `${formatCurrency(totalDirectFundings)} direct + ${formatCurrency(totalIndirectFundings)} indirect`,
      trend: 18.5,
      trendLabel: 'vs last quarter',
    },
    activeReferrers: {
      label: 'Active Referrers',
      value: activeReferrers.toString(),
      subValue: `${participationRate}% participation rate`,
      trend: 12.3,
      trendLabel: 'vs last quarter',
    },
    avgROIPerReferral: {
      label: 'Avg ROI per Referral',
      value: formatCurrency(avgROI),
      subValue: `Based on ${totalReferrals} total referrals`,
      trend: 8.7,
      trendLabel: 'vs last quarter',
    },
    networkGrowth: {
      label: 'Network Growth',
      value: '+24%',
      subValue: 'New referral chains this quarter',
      trend: 24.0,
      trendLabel: 'vs last quarter',
    },
  }
}

/**
 * Calculate secondary executive metrics (mock data with realistic values)
 */
export function calculateSecondaryMetrics(): {
  timeToProductivity: ExecutiveKPI
  retentionRate: ExecutiveKPI
  costSavings: ExecutiveKPI
} {
  return {
    timeToProductivity: {
      label: 'Avg Time-to-Productivity',
      value: '45 days',
      subValue: 'Industry avg: 90 days',
      trend: -50.0, // Negative is good here (faster)
      trendLabel: 'vs industry benchmark',
    },
    retentionRate: {
      label: 'Referral Retention Rate',
      value: '92%',
      subValue: 'vs 78% non-referral hires',
      trend: 14.0,
      trendLabel: 'vs non-referral hires',
    },
    costSavings: {
      label: 'Cost Savings per Hire',
      value: '$45K',
      subValue: 'vs traditional recruiting',
      trend: 32.5,
      trendLabel: 'vs traditional hiring',
    },
  }
}

// Helper function to format currency
function formatCurrency(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}M`
  }
  return `$${amount}K`
}

export { formatCurrency }
